import { useNavigate, useParams } from "react-router-dom";
import {
  Card as MuiCard,
  CardContent as MuiCardContent,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";

import styled from "styled-components/macro";
import { spacing } from "@mui/system";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../hooks/TypeScript/hooks";
import {
  coreDealersAsDict,
  getDealersAll,
  ICoreDealersAsDict,
  resetStatus,
} from "../../../redux/slices/core";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

export default function DealerSearchInput({
  selectValue,
  setSelectValue,
  newTab,
  className,
}: {
  selectValue: ICoreDealersAsDict | null;
  setSelectValue: React.Dispatch<
    React.SetStateAction<ICoreDealersAsDict | null>
  >;
  newTab?: boolean;
  className?: string;
}) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState<string>("");
  const [needRefresh, triggerNeedRefresh] = useState<Boolean>(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const dealerList = useAppSelector(coreDealersAsDict);

  const debouncedSearchValue = GetDebouncedInput(inputValue, 1000);

  const handleDealerChange = (newValue: ICoreDealersAsDict | null) => {
    if (newValue) {
      if (newTab) {
        window.open(`/planning/dealerProfile/${newValue.value}`, "_blank");
      } else {
        navigate(`/planning/dealerProfile/${newValue.value}`, {
          replace: true,
        });
      }
    } else {
      navigate(`/planning/dealerProfile`, { replace: true });
    }
  };

  async function fetchDealers() {
    if (debouncedSearchValue !== "") {
      setLoading(true);
      try {
        await dispatch(getDealersAll(`${debouncedSearchValue}/true`));
      } catch (e) {
        console.log("Error:", e);
      } finally {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    fetchDealers();

    if (needRefresh) {
      triggerNeedRefresh(false);
      dispatch(resetStatus("brands"));
      dispatch(resetStatus("dealer"));
    }

    return () => {
      dispatch(resetStatus("dealers"));
    };
  }, [debouncedSearchValue, needRefresh]);

  return (
    <div className={className} style={{ border: "5px", borderRadius: "5px" }}>
      <Autocomplete
        options={dealerList}
        loading={loading}
        filterOptions={(x) => x}
        value={selectValue}
        onBlur={() => {
          setInputValue("");
        }}
        onChange={(event, newValue) => {
          triggerNeedRefresh(true);
          setSelectValue(newValue);
          if (newValue !== null) handleDealerChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            setInputValue(newInputValue);
          }
        }}
        noOptionsText={loading ? "Loading..." : "No Options"}
        renderOption={(props, options) => {
          return (
            <li {...props} key={options.value}>
              {options.label} | {options.meta.customerName}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Dealer"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
}

const GetDebouncedInput = (value: string, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(id);
    };
  }, [value, delay]);

  return debouncedValue;
};
